import React, { useState, useRef } from 'react';

const SerialNumberInput = ({ name, label, value, onChange, error, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Convert comma-separated string to array
  const serialNumbers = value ? value.split(',').map(s => s.trim()).filter(s => s.length > 0) : [];

  const addSerialNumber = (serial) => {
    if (serial.trim() && !serialNumbers.includes(serial.trim())) {
      const newSerials = [...serialNumbers, serial.trim()];
      onChange(newSerials.join(', '));
    }
  };

  const removeSerialNumber = (index) => {
    const newSerials = serialNumbers.filter((_, i) => i !== index);
    onChange(newSerials.join(', '));
  };

  const handleInputChange = (e) => {
    const inputVal = e.target.value;

    // Check if comma or space was entered
    if (inputVal.includes(',') || inputVal.includes(' ')) {
      const separators = /[, ]+/; // Match commas, spaces, or combinations
      const parts = inputVal.split(separators);
      const completed = parts.slice(0, -1).filter(part => part.trim()); // Remove empty parts
      const remaining = parts[parts.length - 1];

      // Add all completed parts
      completed.forEach(serial => addSerialNumber(serial));

      // Keep the remaining part in input
      setInputValue(remaining);
    } else {
      setInputValue(inputVal);
    }
  };

  const handleInputKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputValue.trim()) {
      e.preventDefault();
      addSerialNumber(inputValue);
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue && serialNumbers.length > 0) {
      e.preventDefault();
      removeSerialNumber(serialNumbers.length - 1);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (inputValue.trim()) {
      addSerialNumber(inputValue);
      setInputValue('');
    }
  };

  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {label} <span className="text-danger">*</span>
      </label>
      <div
        className={`form-control serial-number-input ${isFocused ? 'focused' : ''} ${error ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleContainerClick}
        onKeyDown={(e) => e.key === 'Enter' && handleContainerClick()}
        role="textbox"
        tabIndex={disabled ? -1 : 0}
      >
        <div className="d-flex flex-wrap align-items-center serial-tags">
          {serialNumbers.map((serial, index) => (
            <span key={index} className="badge badge-secondary d-inline-flex align-items-center serial-tag">
              {serial}
              {!disabled && (
                <button
                  type="button"
                  className="btn btn-sm serial-tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSerialNumber(index);
                  }}
                  aria-label={`Remove ${serial}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            id={name}
            name={name}
            className="serial-input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={handleInputBlur}
            disabled={disabled}
            placeholder={serialNumbers.length === 0 ? 'Enter serial numbers (separate with commas, spaces, or Enter)' : ''}
            autoComplete="off"
          />
        </div>
      </div>
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

export default SerialNumberInput;